import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import BreadCumb from "../../components/common/Breadcrumb";
import { ApolloError, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  CLOCK_OUT_TASK_BY_EMPLOYEE_ID,
  DEFECTS,
  PODS_DATA,
  PROJECTS_BY_EMP_ID,
} from "../../api/query";
import Loader from "../../components/common/Loader";
import { format, parse } from "date-fns";
import { useLocation, useNavigate } from "react-router-dom";
import { CREATE_CLOCK_IN_OUT, CREATE_POD_GOAL,  UPDATE_DEFECT } from "../../api/mutation";
import {
  showError,
  showSuccess,
} from "../../components/common/ToastNotification";
import clockOuticon from "../../assets/task/login_svgrepo-com.svg";
import backArrow from "../../assets/task/back_arrow.svg";
import { classForStatus } from "../../components/goals/GoalTaskView";
import DynamicTable from "../../components/common/DynamicTable";
import DefectEmailModal from "../defect/DefectEmailModal";
import InputsDropdown from "../../components/common/InputsDropdown";
import { SortingState } from "@tanstack/react-table";
import { tr } from "date-fns/locale";

const ClockOutTasks: FunctionComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [openDefectEmail, setOpenDefectEmail] = useState("");
  const [autoCreatingDefectId, setAutoCreatingDefectId] = useState<
    string | null
  >(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [mainTable, setMainTable] = useState<any>(null);
  const [mainTableUpdate, setMainTableUpdate] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [defectData, setDefectData] = useState<any[]>([]);
  const [allPodsData, setAllPodsData] = useState<any[]>([]);
  const [gettingLatestDefect, setGettingLatestDefect] = useState(false);
  const [trainingIssueModal, setTrainingIssueModal] = useState<{
    isOpen: boolean;
    defectRow: any | null;
  }>({
    isOpen: false,
    defectRow: null,
  });

  const [selectedTrainingTaskTag, setSelectedTrainingTaskTag] =
    useState<string>("");

    useEffect(() => {
  const isModalOpen =
    Boolean(openDefectEmail) || trainingIssueModal.isOpen;

  if (!isModalOpen) return;

  const body = document.body;
  const html = document.documentElement;

  const originalBodyOverflow = body.style.overflow;
  const originalBodyPaddingRight = body.style.paddingRight;
  const originalHtmlOverflow = html.style.overflow;

  const scrollbarWidth =
    window.innerWidth - document.documentElement.clientWidth;

  body.style.overflow = "hidden";
  html.style.overflow = "hidden";

  if (scrollbarWidth > 0) {
    body.style.paddingRight = `${scrollbarWidth}px`;
  }

  return () => {
    body.style.overflow = originalBodyOverflow;
    body.style.paddingRight = originalBodyPaddingRight;
    html.style.overflow = originalHtmlOverflow;
  };
}, [openDefectEmail, trainingIssueModal.isOpen]);

  const TRAINING_ISSUE_TASK_OPTIONS = [
    {
      key: "validate",
      name: "Triage",
    },
    {
      key: "fix",
      name: "Unblock her now",
    },
    {
      key: "check",
      name: "What was missing",
    },
  ];

  const webhookCalledRef = useRef(false);

  const { userInfo } = useAuth();

  const state = location.state as {
    todayClockInData: any;
  };

  const { todayClockInData } = state || {};

  const employeeName = `${userInfo?.first_name || ""} ${
    userInfo?.last_name || ""
  }`.trim();

  const employeeID = userInfo?.employee_id || "";
  const agencyID = userInfo?.agencies?.id || "";
  const appUserID = userInfo?.app_user_id || "";

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const [createPodGoal] = useMutation(CREATE_POD_GOAL, {
    fetchPolicy: "no-cache",
  });

  const [updateDefect] = useMutation(UPDATE_DEFECT, {
  fetchPolicy: "no-cache",
});

  const [projects, setProjects] = useState<any[]>([]);

  const [createClockIn, { loading: createClockInLoading }] = useMutation(
    CREATE_CLOCK_IN_OUT,
    {
      fetchPolicy: "no-cache",
    },
  );

  // ---------------------------------------------------------------------------
  // Action Type Configuration
  // ---------------------------------------------------------------------------

  /**
   * Configuration for automatic Pod Goal creation.
   *
   * action_type:
   *   Value sent to the API as action_type.
   *
   * template_tag:
   *   Template tag used by backend to determine which template to create.
   */
  const DEFECT_ACTION_CONFIG: Record<
    string,
    {
      action_type: string;
      template_tag: string;
    }
  > = {
    Bug: {
      action_type: "Bug",
      template_tag: "bug-fix",
    },

    "Training Issue": {
      action_type: "Training Issue",
      template_tag: "training-issue",
    },

    // "Enhancement": {
    //   action_type: "Enhancement",
    //   template_tag: "enhancements",
    // },
  };


      const {  loading: projectsDataLoading } = useQuery(
        PROJECTS_BY_EMP_ID,
        {
          fetchPolicy: "no-cache",
          variables: {
            emp_id: "1000004",
            organization_id: "3",
          },
              onCompleted: (data: any) => {
          const options =
            data?.projects_by_emp_id?.map((emp: any) => ({
              key: emp.id,
              name: emp.name,
            })) || [];
    
          setProjects(options);
        },
        },
      );

  const defectActionOptions = Object.keys(DEFECT_ACTION_CONFIG).map(
    (actionType) => ({
      key: actionType,
      name: actionType,
    }),
  );

  const podOptions = allPodsData
  .filter((pod: any) => {
    const podName = String(pod?.pod_name || "").trim().toLowerCase();
    return podName && podName !== "all";
  })
  .map((pod: any) => ({
    key: String(pod?.pod_id),
    name: pod?.pod_name,
  }));

  // ---------------------------------------------------------------------------
  // API Queries
  // ---------------------------------------------------------------------------

  const [
    getEmployeeClockOutTasks,
    { data: clockOutTasks, loading: clockOutTasksLoading },
  ] = useLazyQuery(CLOCK_OUT_TASK_BY_EMPLOYEE_ID, {
    fetchPolicy: "no-cache",
  });

  const [getDefects, { data: defectsData, loading: defectsDataLoading }] =
    useLazyQuery(DEFECTS, {
      fetchPolicy: "no-cache",
    });

  const [fetchPods, { loading: fetchPodsLoading }] = useLazyQuery(PODS_DATA, {
    fetchPolicy: "no-cache",
  });

  // ---------------------------------------------------------------------------
  // Get Latest Defect Webhook
  // ---------------------------------------------------------------------------


const handlePodChange = async (
  defectRow: any,
  selectedOption: any
) => {
  const selectedPodId =
    typeof selectedOption === "string"
      ? selectedOption
      : selectedOption?.key;

  if (!selectedPodId) return;

  const selectedPod = allPodsData.find(
    (pod: any) => String(pod?.pod_id) === String(selectedPodId)
  );

  if (!selectedPod) return;

  const defectId = defectRow?.id ? Number(defectRow.id) : null;

  if (!defectId) {
    showError("Invalid defect ID.");
    return;
  }

  try {
    const response = await updateDefect({
      variables: {
        input: {
          action_type: defectRow?.action_type || null,
          title: defectRow?.title || "",
          created_by: parseInt(employeeID),
          is_issue: true,
          is_confirmed: true,

          // New pod
          pod_id: Number(selectedPodId),
        },
        id: defectId,
      },
    });

    const result = response?.data?.update_defect;

    if (
      result?.status_code &&
      result.status_code !== 200
    ) {
      showError(result?.message || "Failed to update Pod.");
      return;
    }

    // Immediately update table UI
    setDefectData((prevData) =>
      prevData.map((item: any) =>
        Number(item?.id) === defectId
          ? {
              ...item,
              pod_id: Number(selectedPodId),
              pod_name: selectedPod?.pod_name,
              pod_members: selectedPod?.members,
            }
          : item
      )
    );

    showSuccess("Pod updated successfully.");
  } catch (error) {
    console.error("Failed to update defect pod:", error);

    if (error instanceof ApolloError) {
      showError(error.message);
    } else {
      showError("Failed to update Pod. Please try again.");
    }
  }
};

  const handleGetLatestDefect = async () => {
    if (!employeeID || gettingLatestDefect) return;

    setGettingLatestDefect(true);

    try {
      const response = await fetch(
        "https://n8n-rrjie-clone-u41909.vm.elestio.app/webhook/cs-night-defect-create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            employee_id: Number(employeeID),
            employee_name: employeeName,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Webhook failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log("Webhook response:", data);

      // Fetch latest defects after webhook completes
      await fetchDefectsData();

      showSuccess("Latest defect fetched successfully.");
    } catch (error) {
      console.error("Webhook error:", error);
      showError("Failed to get the latest defect. Please try again.");
    } finally {
      setGettingLatestDefect(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Fetch Today's Pods
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!agencyID) return;

    const today = format(new Date(), "yyyy-MM-dd");

    fetchPods({
      variables: {
        agency_id: parseInt(agencyID),
        start_date: today,
        end_date: today,
      },
    })
      .then((response: any) => {
        const data = response?.data?.pods_data?.data || [];
        setAllPodsData(data);
      })
      .catch((error) => {
        console.error("Failed to fetch pods:", error);
        setAllPodsData([]);
      });
  }, [agencyID, fetchPods]);

  // ---------------------------------------------------------------------------
  // Defect Data Mapper
  // ---------------------------------------------------------------------------

  const mapDefects = (defects: any[]) => {
    return defects.map((defect: any) => {
      const pod = allPodsData.find(
        (item: any) => item?.pod_id == defect?.pod_id,
      );

      return {
        ...defect,
        title: defect?.title,
        severity: defect?.severity,
        created_on: defect?.created_on
          ? format(defect.created_on, "MM-dd-yyyy")
          : "",
        id: defect?.id,
        pod_name: pod?.pod_name,
        action_type: null,
        pod_members: pod?.members,
      };
    });
  };

  // ---------------------------------------------------------------------------
  // Fetch Employee Tasks + Defects
  // ---------------------------------------------------------------------------

  const fetchDefectsData = async () => {
    try {
      const response = await getDefects({
        variables: {
          page: 1,
          page_size: 100,
          is_confirmed: true,
          is_issue: true,
          is_resolved: false,
          replied_by_employee_id: Number(employeeID),
        },
      });

      const defects =
        response?.data?.defects?.defects?.filter(
          (it: any) => !it?.is_goal_exist,
        ) || [];

      setDefectData(mapDefects(defects));
    } catch (error) {
      console.error("Failed to fetch defects:", error);
      setDefectData([]);
    }
  };

  useEffect(() => {
    if (!employeeID || allPodsData.length === 0) return;

    getEmployeeClockOutTasks({
      variables: {
        employee_id: parseInt(employeeID),
      },
    });

    fetchDefectsData();
  }, [employeeID, allPodsData]);

  // ---------------------------------------------------------------------------
  // Clock Out
  // ---------------------------------------------------------------------------

  async function handleClockOut() {
    const tasks = clockOutTasks?.clock_out_tasks_by_employee_id?.tasks || [];

    const hasTimeSpent = tasks.some(
      (task: any) =>
        (task?.today_spent_hours && task.today_spent_hours !== "0") ||
        (task?.today_spent_minutes && task.today_spent_minutes !== "0"),
    );

    if(defectData?.length > 0 && canGetLatestDefect){
          showError(
        "Please assign defect before clocking out.",
      );
      return;
    }

    if (!hasTimeSpent) {
      showError(
        "Please update today's task and add time spent before clocking out.",
      );
      return;
    }

    const now = new Date();

    const pad = (value: number) => String(value).padStart(2, "0");

    const localTime = `${now.getFullYear()}-${pad(
      now.getMonth() + 1,
    )}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(
      now.getMinutes(),
    )}:${pad(now.getSeconds())}.000Z`;

    const clockInInput = {
      employees_id: parseInt(employeeID),
      check_in_time: todayClockInData?.check_in_time,
      check_out_time: localTime,
      is_check_in: false,
      agencies_id: parseInt(agencyID),
      checked_by: employeeID,
      minutes: todayClockInData?.minutes,
      hours: todayClockInData?.hours,
      created_by: parseInt(employeeID),
      updated_by: parseInt(employeeID),
      app_user_id: appUserID,
      id: parseInt(todayClockInData?.id),
    };

    try {
      const response = await createClockIn({
        variables: {
          input: clockInInput,
        },
      });

      const result = response?.data?.create_clock_in_out;

      if (result?.errors?.length === 0) {
        showSuccess("Clock Out successful");
        navigate("/actions");
        return;
      }

      if (result?.errors?.length > 0) {
        showError(result.errors[0]?.messages?.[0]);
      }
    } catch (error) {
      if (error instanceof ApolloError) {
        showError(error.message);
      } else {
        showError("Failed to clock out. Please try again.");
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Create Pod Goal from Defect
  // ---------------------------------------------------------------------------

  async function handleCreatePodGoal(
    defectRow: any,
    actionType: string,
    taskTag?: string,
  ) {
    if (autoCreatingDefectId) return;

    const actionConfig = DEFECT_ACTION_CONFIG[actionType];

    if (!actionConfig) {
      showError(`Unsupported action type: ${actionType}`);
      return;
    }

    const defectId = String(defectRow?.id || "");

    if (!defectId) {
      showError("Invalid defect ID.");
      return;
    }

    setAutoCreatingDefectId(defectId);
    const projectid = projects?.find((it:any) => it.name == defectRow?.project_name)?.key

    try {
      const today = format(new Date(), "yyyy-MM-dd");

      const response = await createPodGoal({
        variables: {
             project_id:Number(projectid),
          pod_id: defectRow?.pod_id ? Number(defectRow.pod_id) : null,

title:
  actionConfig.action_type +
  (defectRow?.agency_name && defectRow?.screen_name
    ? ` for ${defectRow.agency_name} on screen ${defectRow.screen_name}`
    : defectRow?.agency_name
      ? ` for ${defectRow.agency_name}`
      : defectRow?.screen_name
        ? ` on screen ${defectRow.screen_name}`
        : ""),


          action_type: actionConfig.action_type,

          defect_id: Number(defectRow.id),

          description: defectRow?.description || "",

          start_date: today,

          task_assignees: null,

          template_tag: actionConfig.template_tag,
          ...(actionType === "Training Issue" && taskTag
            ? {
                task_tag: taskTag,
              }
            : {}),
        },
      });

      const result = response?.data?.create_pod_goal;

      if (result?.status_code !== 200) {
        showError(result?.message || `Failed to create ${actionType} goal.`);
        return;
      }

      showSuccess(
        result?.message || `${actionType} goal created successfully.`,
      );

      // Refresh clock-out tasks
      await getEmployeeClockOutTasks({
        variables: {
          employee_id: Number(employeeID),
        },
      });

      // Refresh defects
      await fetchDefectsData();
    } catch (error) {
      console.error(`Failed to create ${actionType} goal:`, error);

      if (error instanceof ApolloError) {
        showError(error.message);
      } else {
        showError(`Failed to create ${actionType} goal. Please try again.`);
      }
    } finally {
      setAutoCreatingDefectId(null);
    }
  }

  // ---------------------------------------------------------------------------
  // Table Columns
  // ---------------------------------------------------------------------------

  const columns = [
    {
      key: "reporter_name",
      name: "Reporter Name",
      id: "reporter_name",
      header: "Reporter Name",
      accessorKey: "reporter_name",
      enableSorting: true,

      sortingFn: (rowA: any, rowB: any, columnId: any) => {
        const valueA = String(rowA.getValue(columnId) || "").toLowerCase();

        const valueB = String(rowB.getValue(columnId) || "").toLowerCase();

        return valueA.localeCompare(valueB);
      },

      cell: ({ row }: any) => {
        return (
          <div className="flex items-center gap-[8px] theme_btn_item">
            <span
              className="defact-click-item cursor-pointer"
              onClick={() => setOpenDefectEmail(row.original.id)}
            >
              {row.original.reporter_name}
            </span>
          </div>
        );
      },
    },

    {
      key: "agency_name",
      name: "Business Name",
      id: "agency_name",
      header: "Business Name",
      accessorKey: "agency_name",
      enableSorting: true,

      sortingFn: (rowA: any, rowB: any, columnId: any) => {
        const valueA = String(rowA.getValue(columnId) || "").toLowerCase();

        const valueB = String(rowB.getValue(columnId) || "").toLowerCase();

        return valueA.localeCompare(valueB);
      },
    },

    {
      key: "created_on",
      name: "Reported Date",
      id: "created_on",
      header: "Reported Date",
      accessorKey: "created_on",
      enableSorting: true,

      sortingFn: (rowA: any, rowB: any) => {
        const startDateA = rowA?.getValue("created_on");
        const startDateB = rowB?.getValue("created_on");

        const defaultDate = new Date(0);

        const dateA = startDateA
          ? parse(startDateA, "MM-dd-yyyy", new Date())
          : defaultDate;

        const dateB = startDateB
          ? parse(startDateB, "MM-dd-yyyy", new Date())
          : defaultDate;

        if (isNaN(dateA.getTime())) {
          dateA.setTime(defaultDate.getTime());
        }

        if (isNaN(dateB.getTime())) {
          dateB.setTime(defaultDate.getTime());
        }

        return dateA.getTime() - dateB.getTime();
      },
    },

{
  key: "pod_name",
  name: "Pod Name",
  id: "pod_name",
  header: "Pod Name",
  accessorKey: "pod_name",
  enableSorting: true,

  sortingFn: (rowA: any, rowB: any, columnId: any) => {
    const valueA = String(
      rowA.getValue(columnId) || ""
    ).toLowerCase();

    const valueB = String(
      rowB.getValue(columnId) || ""
    ).toLowerCase();

    return valueA.localeCompare(valueB);
  },

  cell: ({ row }: any) => {
    const members = row?.original?.pod_members
      ?.map(
        (member: any) =>
          `${member.first_name} ${member.last_name}`
      )
      .join(", ");

    return (
      <div
        className="flex items-center gap-[8px] theme_btn_item"
        title={members}
      >
        <InputsDropdown
          label=""
          options={podOptions}
          placeHolder={
            row.original?.pod_name || "Select Pod"
          }
          onSelect={(selectedOption: any) => {
            handlePodChange(
              row.original,
              selectedOption
            );
          }}
        />
      </div>
    );
  },
},
    {
      key: "screen_name",
      name: "Generated Error Name",
      id: "screen_name",
      header: "Generated Error Name",
      accessorKey: "screen_name",
      enableSorting: true,

      sortingFn: (rowA: any, rowB: any, columnId: any) => {
        const valueA = String(rowA.getValue(columnId) || "").toLowerCase();

        const valueB = String(rowB.getValue(columnId) || "").toLowerCase();

        return valueA.localeCompare(valueB);
      },
    },

    {
      key: "severity",
      name: "Severity",
      id: "severity",
      header: "Severity",
      accessorKey: "severity",
      enableSorting: true,
    },

    // -------------------------------------------------------------------------
    // Action Column
    // -------------------------------------------------------------------------

    {
      key: "action",
      name: "Action",
      id: "action",
      header: "Action",
      accessorKey: "action",
      hideSearch: true,

      cell: ({ row }: any) => {
        const defectId = String(row.original.id);

        const isCreating = autoCreatingDefectId === defectId;

        return (
          <div className="flex items-center gap-[8px] theme_btn_item">
            {isCreating ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />

                <span>Creating…</span>
              </div>
            ) : (
              <InputsDropdown
                label=""
                options={defectActionOptions}
                placeHolder={row.original.action_type || "Select action"}
                onSelect={(selectedOption: any) => {
                  const actionType =
                    typeof selectedOption === "string"
                      ? selectedOption
                      : selectedOption?.key || selectedOption?.name;

                  if (actionType && DEFECT_ACTION_CONFIG[actionType]) {
                    if (actionType === "Training Issue") {
                      setSelectedTrainingTaskTag("");

                      setTrainingIssueModal({
                        isOpen: true,
                        defectRow: row.original,
                      });

                      return;
                    }

                    handleCreatePodGoal(row.original, actionType);
                  }
                }}
              />
            )}
          </div>
        );
      },
    },
  ];

  // ---------------------------------------------------------------------------
  // Combined Tasks
  // ---------------------------------------------------------------------------

  const combinedTasks = (
    clockOutTasks?.clock_out_tasks_by_employee_id?.tasks || []
  ).map((task: any) => ({
    ...task,
  }));

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const allowedEmployeeIds = [
    1000141, 1000098, 1000128, 1000143, 1000144
  ];

  const canGetLatestDefect = allowedEmployeeIds.includes(Number(employeeID));

  return (
    <>
      <div className="w-full relative min-h-screen overflow-hidden shrink-0 flex flex-row items-start justify-start flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4 box-border text-left text-base text-gray font-manrope gap-[24px]">
        <div className="grid w-full gap-[24px]">
          <div className="self-stretch rounded flex flex-col items-start justify-start z-[0] text-base text-dimgray text-left">
            <BreadCumb clientName={employeeName} />
          </div>

          <div className="self-stretch flex flex-row items-center justify-start gap-[5px] z-[1] text-2xl">
            <img
              className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
              alt=""
              src={backArrow}
              onClick={() => navigate("/actions")}
            />

            <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
              Clock Out
            </h3>
          </div>
        </div>

        <div className="m-0 self-stretch rounded-radius-radius-lg w-full bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
          {canGetLatestDefect && (
            <>
              <div className="self-stretch flex flex-row items-center justify-between gap-4 mb-4">
                <h5 className="my-0 relative inline-block max-w-full dark:text-white">
                  <b>Today’s Report ({defectData?.length || 0})</b>
                </h5>

                <button
                  type="button"
                  onClick={handleGetLatestDefect}
                  disabled={gettingLatestDefect}
                  className="cursor-pointer [border:none] py-2.5 px-5 bg-surface-primary-surface rounded-radiuscomponent-button flex flex-row items-center justify-center gap-2 text-sm font-semibold text-surface-bg-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {gettingLatestDefect ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      <span>Getting Latest Defect...</span>
                    </>
                  ) : (
                    <span>Get Latest Defect</span>
                  )}
                </button>
              </div>
              {defectData?.length > 0 && (
                <>
                  <div className="self-stretch grid flex-row items-start justify-start clockout-main-table-item">
                    <div className="flex-1 flex flex-row items-start justify-start">
                      <h5 className="my-0 mb-4 flex-1 relative inline-block max-w-full dark:text-white">
                        <b>Defect List</b>
                      </h5>
                    </div>

                    <DynamicTable
                      filteredData={defectData}
                      selectedColumns={columns}
                      sorting={sorting}
                      setSorting={setSorting}
                      selectedRows={selectedRows}
                      setSelectedRows={setSelectedRows}
                      setMainTable={setMainTable}
                      setMainTableUpdate={setMainTableUpdate}
                      showPagination={false}
                    />
                  </div>

                  <div className="self-stretch flex flex-row items-start justify-start">
                    <div className="flex-1 flex flex-row items-start justify-start">
                      <h5 className="my-0 flex-1 relative inline-block max-w-full dark:text-white">
                        <b>Today’s Tasks</b>
                      </h5>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          <div className="self-stretch flex flex-col items-start justify-start gap-4 text-base">
            <div className="grid grid-cols-3 mq1425:grid-cols-3 mq1275:grid-cols-3 mq900:grid-cols-2 mq575:grid-cols-1 flex-row flex-wrap items-center justify-start gap-[16px]">
              {clockOutTasks &&
              clockOutTasks?.clock_out_tasks_by_employee_id?.status_code ===
                200 &&
              combinedTasks?.length > 0 ? (
                combinedTasks.map((task: any) => (
                  <div
                    className="cursor-pointer darkbox-border-cts-dv flex-1 rounded self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] dark:bg-slate-700 bg-text-text-tertiary overflow-hidden inline flex-row items-center justify-start py-2.5 gap-[8px] border-[1px] border-solid border-surface-border mq450:flex-wrap pt-4 px-4 pb-2.5 gap-2"
                    key={task?.id}
                    onClick={() => {
                      navigate("/clock_out_task", {
                        state: {
                          task,
                        },
                      });
                    }}
                  >
                    <div className="self-stretch flex flex-col items-start justify-start">
                      <div className="self-stretch flex flex-row items-center justify-start">
                        <div className="flex-1 flex flex-row items-center justify-start">
                          <div className="relative font-semibold capitalize tasktitle_card mb-1">
                            Goal : {task?.goal_name}
                          </div>
                        </div>
                      </div>

                      <div className="self-stretch flex flex-row items-center justify-start">
                        <div className="flex-1 flex flex-row items-center justify-start">
                          <div className="relative font-semibold capitalize tasktitle_card mb-1">
                            Task : {task?.title || task?.task}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="self-stretch flex flex-col items-start justify-center gap-3 text-dimgray">
                      <div className="self-stretch flex flex-row items-start justify-start">
                        <div className="flex-1 relative">
                          {task?.static_task_type_name}
                        </div>
                      </div>

                      <hr className="bdr-custom-taskcrad" />

                      <div className="self-stretch flex flex-row items-start justify-start gap-2">
                        <div className="w-[120px] relative inline-block shrink-0">
                          Due Date
                        </div>

                        <div className="relative font-semibold text-gray">
                          {task?.end_datetime
                            ? format(task.end_datetime, "MM-dd-yyyy")
                            : "-"}
                        </div>
                      </div>

                      <div className="self-stretch flex flex-row items-center justify-start gap-2">
                        <div className="w-[120px] relative inline-block shrink-0">
                          Assigned By
                        </div>

                        <div className="relative font-semibold text-gray">
                          {task?.created_by_name}
                        </div>
                      </div>

                      {task?.today_spent_hours > 0 ||
                      task?.today_spent_minutes > 0 ? (
                        <div className="self-stretch flex flex-row items-center justify-start gap-2">
                          <div className="w-[120px] relative inline-block shrink-0">
                            Today's Time Spent
                          </div>

                          <div className="relative font-semibold text-gray">
                            {`${task?.today_spent_hours}H ${task?.today_spent_minutes}M`}
                          </div>
                        </div>
                      ) : (
                        <div className="w-full">
                          <p className="relative font-semibold italic text-sm textred_custom_dv my-0">
                            No time spent After Clock in
                          </p>
                        </div>
                      )}

                      <hr className="bdr-custom-taskcrad" />

                      <div className="self-stretch flex flex-row items-center justify-between gap-0">
                        <div
                          className={`rounded-[50px] bg-antiquewhite border-orange border-solid border-[1px] flex flex-row items-center justify-center py-1 px-4 ${classForStatus(
                            task?.status_name,
                          )}`}
                        >
                          <div className="relative">
                            {task?.status_name || task?.status}
                          </div>
                        </div>

                        {task?.task_tags?.length > 0 &&
                          task.task_tags.map((tag: any, index: number) => (
                            <div
                              key={`${tag?.tags_name}-${index}`}
                              className="rounded-[100px] bg-crimson flex flex-row items-center justify-center py-1 px-2 text-sm text-white right_status_clockout"
                            >
                              <div className="relative">{tag?.tags_name}</div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex self-stretch flex-col min-w-full w-full border-[1px] border-solid border-surface-border p-2 justify-center text-center">
                  <p className="text-sm dark:bg-white">No task Available</p>
                </div>
              )}
            </div>

            <button
              className="red-clockout-btn cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface rounded-radiuscomponent-button flex flex-row items-center justify-center gap-[12px]"
              type="button"
              onClick={handleClockOut}
            >
              <img src={clockOuticon} className="w-10 h-10" alt="" />

              <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block uppercase font-semibold">
                Clock Out
              </div>
            </button>
          </div>
        </div>
      </div>

      {(clockOutTasksLoading ||
        createClockInLoading ||
        defectsDataLoading ||
        fetchPodsLoading ||projectsDataLoading ) && <Loader />}

      <DefectEmailModal
        defectsData={defectsData}
        isOpen={openDefectEmail}
        onClose={() => setOpenDefectEmail("")}
      />
      {trainingIssueModal.isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="w-[420px] max-w-[90%] rounded-lg bg-white dark:bg-slate-800 p-6 shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className=" mb-0 my-0 font-bold text-xl-dv text-black dark:text-white">
                Select Training Issue Task
              </h2>

              <button
                type="button"
                onClick={() => {
                  setTrainingIssueModal({
                    isOpen: false,
                    defectRow: null,
                  });
                  setSelectedTrainingTaskTag("");
                }}
                className="text-gray-400 cta-red transition mt-0.5 ml-4 flex-shrink-0 pt-1"
              >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>

            <div className="flex flex-col gap-3">
              {TRAINING_ISSUE_TASK_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={async () => {
                    const defectRow = trainingIssueModal.defectRow;

                    setTrainingIssueModal({
                      isOpen: false,
                      defectRow: null,
                    });

                    setSelectedTrainingTaskTag(option.key);

                    await handleCreatePodGoal(
                      defectRow,
                      "Training Issue",
                      option.key,
                    );

                    setSelectedTrainingTaskTag("");
                  }}
                  className="w-full rounded-md border border-surface-border px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-slate-700 dark:text-white clockout-button-item-dv"
                >
                  {option.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClockOutTasks;
